import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Edit2, Trash2, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Sortable desktop product row (used inside <SortableContext>).
 * Whole row is draggable via the grip handle in the first column.
 */
export default function ProductRow({ product, idx, total, checked, onCheck, onEdit, onDelete, onMoveUp, onMoveDown }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    background: isDragging ? "#FFE7F1" : undefined,
  };
  const cats = (product.categories && product.categories.length ? product.categories : [product.category]).filter(Boolean);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-testid={`admin-row-${product.slug}`}
      className={`border-t border-[#E5DBD8] ${checked ? "bg-[#FFE7F1]/60" : ""}`}
      {...attributes}
    >
      <td
        ref={setActivatorNodeRef}
        {...listeners}
        className="p-2 text-[#5C5555] cursor-grab active:cursor-grabbing touch-none"
        data-testid={`product-drag-${product.slug}`}
        title="Drag to reorder"
        aria-label={`Drag ${product.name} to reorder`}
      >
        <GripVertical className="w-4 h-4"/>
      </td>
      <td className="p-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheck(product.id, e.target.checked)}
          data-testid={`product-check-${product.slug}`}
          className="w-4 h-4 accent-[#7A1F2B] cursor-pointer"
        />
      </td>
      <td className="p-3">
        <div className="flex flex-col items-center gap-0.5">
          <button
            type="button"
            onClick={() => onMoveUp(idx)}
            disabled={idx === 0}
            data-testid={`product-up-${product.slug}`}
            className="w-7 h-7 rounded border border-[#E5DBD8] text-[#7A1F2B] flex items-center justify-center hover:bg-[#FFE7F1] disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Move up"
          >
            <ArrowUp className="w-3.5 h-3.5"/>
          </button>
          <span className="text-[10px] text-[#5C5555] font-doodle">#{idx + 1}</span>
          <button
            type="button"
            onClick={() => onMoveDown(idx)}
            disabled={idx === total - 1}
            data-testid={`product-down-${product.slug}`}
            className="w-7 h-7 rounded border border-[#E5DBD8] text-[#7A1F2B] flex items-center justify-center hover:bg-[#FFE7F1] disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Move down"
          >
            <ArrowDown className="w-3.5 h-3.5"/>
          </button>
        </div>
      </td>
      <td className="p-3"><img src={product.images[0]} alt="" className="w-14 h-14 object-cover"/></td>
      <td className="p-3 font-serif">{product.name}</td>
      <td className="p-3 capitalize">
        {cats.map((c) => (
          <span key={c} className="inline-block bg-[#FFE7F1] text-[#7A1F2B] text-xs px-2 py-0.5 mr-1 mb-1 rounded-full">{c}</span>
        ))}
      </td>
      <td className="p-3">₹{product.price.toLocaleString("en-IN")}</td>
      <td className="p-3">{product.stock}</td>
      <td className="p-3">{product.featured ? "★" : "—"}</td>
      <td className="p-3 flex gap-2">
        <button onClick={() => onEdit(product)} data-testid={`admin-edit-${product.slug}`} className="text-[#7A1F2B] hover:text-[#5C1520]"><Edit2 className="w-4 h-4"/></button>
        <button onClick={() => onDelete(product)} data-testid={`admin-delete-${product.slug}`} className="text-[#7A1F2B] hover:text-[#5C1520]"><Trash2 className="w-4 h-4"/></button>
      </td>
    </tr>
  );
}
